import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import SendIcon from "@mui/icons-material/Send";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";
import useWhatsappToUser from "../../../hooks/useWhatsappToUser";
import WhatsappToUserComposer from "../../../composers/WhatsappToUserComposer";
import useMainContentDimensions from "../../../utils/mainContentDimensions";

import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

function WhatsappToUserInner() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { width: mainContentWidth } = useMainContentDimensions();
  const { addWhatsappToUser, getWhatsappTemplates } = useWhatsappToUser();

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [message, setMessage] = useState("");
  const [mobileNos, setMobileNos] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      const data = await getWhatsappTemplates();
      setTemplates(data || []);
    };
    fetchTemplates();
  }, []);

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);
    const selected = templates.find((item) => item.whatsappTemplateId === templateId);
    if (selected) {
      setMessage(selected.content || "");
    } else {
      setMessage("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mobileNos.trim()) {
      dispatch(showErrorSnackbar("Please enter mobile numbers"));
      return;
    }
    // if (!message.trim()) {
    //   dispatch(showErrorSnackbar("Please enter or select a message template"));
    //   return;
    // }

    try {
      setIsSubmitting(true);
      await addWhatsappToUser({
        templateId: selectedTemplateId,
        message: message,
        mobileNos: mobileNos,
      });
      dispatch(showSuccessSnackbar("WhatsApp message sent successfully!"));
      handleReset();
    } catch (err) {
      dispatch(showErrorSnackbar("Failed to send WhatsApp message"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedTemplateId("");
    setMessage("");
    setMobileNos("");
  };

  return (
    <React.Fragment>
      <Helmet title={t("send_whatsapp_to_users") || "Send WhatsApp to Users"} />
      <Card sx={{ width: "100%", maxWidth: mainContentWidth - 64, mx: "auto", mt: 2 }}>
        <CardHeader
          title={
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {t("whatsapp_to_users") || "Whatsapp To Users"}
            </Typography>
          }
          subheader={
            <Breadcrumbs aria-label="Breadcrumb" mt={1}>
              <Typography color="textSecondary">WhatsApp</Typography>
              <Typography color="textPrimary">Send WhatsApp to Users</Typography>
            </Breadcrumbs>
          }
        />
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={8}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Select Template"
                  value={selectedTemplateId}
                  onChange={handleTemplateChange}
                  variant="outlined"
                  //helperText="Choose a WhatsApp template from the template table"
                >
                  <MenuItem value="">
                    <em>-- Select Template --</em>
                  </MenuItem>
                  {templates.map((tpl) => (
                    <MenuItem key={tpl.whatsappTemplateId} value={tpl.whatsappTemplateId}>
                      {tpl.whatsappTemplateName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Message Content"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  variant="outlined"
                  placeholder="Template content will appear here when selected, or type custom message..."
                />
              </Grid> */}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Mobile Numbers"
                  value={mobileNos}
                  onChange={(e) => setMobileNos(e.target.value)}
                  variant="outlined"
                  placeholder="Enter mobile numbers (separated by comma or new line)... e.g. 919876543210, 919876543211"
                  // helperText="Type destination mobile numbers to send WhatsApp broadcast"
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={2} mt={1}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={
                      isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />
                    }
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Send WhatsApp"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="large"
                    startIcon={<RestartAltIcon />}
                    onClick={handleReset}
                    disabled={isSubmitting}
                  >
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </React.Fragment>
  );
}

function WhatsappToUser() {
  return (
    <WhatsappToUserComposer>
      <WhatsappToUserInner />
    </WhatsappToUserComposer>
  );
}

export default WhatsappToUser;
